import update from 'immutability-helper';
import moment from "moment";

import { CACHE_ACTIONS } from '~/constants/cache-actions.constants';
import { LocalCacheHandler } from '~/utils/local-cache-handler';

import {
    LOAD_BANK_STATEMENTS,
    IMPORT_BANK_STATEMENTS,
    CREATE_RECONCILIATION,
    REMOVE_RECONCILIATION,
    GET_SUGGESTIONS,
    BULK_DELETE,
    AUTO_RECONCILE,
    GET_STATS,
    SET_DRAWER,
    CREATE_AND_RECONCILE,
    LOAD_IMPORTS,
    DELETE_IMPORT,
    IGNORE_STATEMENTS,
    UNIGNORE_STATEMENTS,
    SET_PENDING_MATCH,
} from './actions';

const DEFAULT_CACHE_KEY = CACHE_ACTIONS.bankReconciliation;

export const defaultState = {
    data: [],
    filter: {
        from: moment().format("YYYY-MM-01"),
        to: moment().endOf("month").format("YYYY-MM-DD"),
        status: "all",
        selectedBankId: null,
    },
    isLoading: false,
    isAutoReconciling: false,
    stats: {
        total: 0,
        pending: 0,
        reconciled: 0,
        exact_matches: 0,
        progress: 0,
    },
    drawer: {
        visible: false,
        data: null,
        suggestions: [],
        isLoadingSuggestions: false,
        suggestionsLoaded: false,
        recordTypes: ['mensalidades', 'extras', 'comissoes'],
    },
    pendingMatches: {},  // { statementId: selectedItem }
    imports: {
        data: [],
        isLoading: false,
    },
};

const ACTION_HANDLERS = {
    [DEFAULT_CACHE_KEY]: (state, action) => {
        return action.payload;
    },

    [LOAD_BANK_STATEMENTS.INIT]: (state, action) => {
        return update(state, {
            isLoading: { $set: true },
        });
    },

    [LOAD_BANK_STATEMENTS.SUCCESS]: (state, action) => {
        return update(state, {
            isLoading: { $set: false },
            data: { $set: action.payload.data.data },
            filter: { $set: action.meta.previousAction.payload.filter },
        });
    },

    [LOAD_BANK_STATEMENTS.FAIL]: (state, action) => {
        return update(state, {
            isLoading: { $set: false },
        });
    },

    [IMPORT_BANK_STATEMENTS.INIT]: (state, action) => {
        return update(state, {
            isLoading: { $set: true },
        });
    },

    [IMPORT_BANK_STATEMENTS.SUCCESS]: (state, action) => {
        return update(state, {
            isLoading: { $set: false },
        });
    },

    [IMPORT_BANK_STATEMENTS.FAIL]: (state, action) => {
        return update(state, {
            isLoading: { $set: false },
        });
    },

    // OPTIMIZED: Use index-based update instead of O(n) map
    [CREATE_RECONCILIATION.SUCCESS]: (state, action) => {
        var statementId = action.meta.previousAction.statementId;
        var index = state.data.findIndex(function(item) { return item.id === statementId; });

        if (index === -1) {
            // Item not found, just update drawer
            return update(state, {
                drawer: {
                    visible: { $set: false },
                    data: { $set: null },
                },
            });
        }

        var reconciliationData = action.payload.data.data;
        var reconciliationsArray = Array.isArray(reconciliationData) ? reconciliationData : [reconciliationData];

        return update(state, {
            data: {
                [index]: {
                    status: { $set: 'reconciled' },
                    reconciliations: { $set: reconciliationsArray },
                },
            },
            drawer: {
                visible: { $set: false },
                data: { $set: null },
            },
        });
    },

    // OPTIMIZED: Use index-based update instead of O(n) map
    [REMOVE_RECONCILIATION.SUCCESS]: (state, action) => {
        var statementId = action.meta.previousAction.statementId;
        var index = state.data.findIndex(function(item) { return item.id === statementId; });

        if (index === -1) return state;

        return update(state, {
            data: {
                [index]: {
                    status: { $set: 'pending' },
                    reconciliations: { $set: [] },
                },
            },
        });
    },

    [GET_SUGGESTIONS.INIT]: (state, action) => {
        return update(state, {
            drawer: {
                isLoadingSuggestions: { $set: true },
                suggestionsLoaded: { $set: false },
                suggestions: { $set: [] },
            },
        });
    },

    [GET_SUGGESTIONS.SUCCESS]: (state, action) => {
        return update(state, {
            drawer: {
                isLoadingSuggestions: { $set: false },
                suggestionsLoaded: { $set: true },
                suggestions: { $set: action.payload.data.data },
            },
        });
    },

    [GET_SUGGESTIONS.FAIL]: (state, action) => {
        return update(state, {
            drawer: {
                isLoadingSuggestions: { $set: false },
                suggestionsLoaded: { $set: true },
            },
        });
    },

    [SET_DRAWER]: (state, action) => {
        return update(state, {
            drawer: { $merge: action.payload },
        });
    },

    [SET_PENDING_MATCH]: (state, action) => {
        var newMatches = Object.assign({}, state.pendingMatches);
        if (action.payload.selectedItem) {
            newMatches[action.payload.statementId] = action.payload.selectedItem;
        } else {
            delete newMatches[action.payload.statementId];
        }
        return update(state, {
            pendingMatches: { $set: newMatches },
        });
    },

    [BULK_DELETE.INIT]: (state, action) => {
        return update(state, {
            isLoading: { $set: true },
        });
    },

    // OPTIMIZED: Use Set for O(1) lookup instead of O(n) indexOf
    [BULK_DELETE.SUCCESS]: (state, action) => {
        var deletedIds = action.meta.previousAction.ids;
        var deletedSet = {};
        deletedIds.forEach(function(id) { deletedSet[id] = true; });

        return update(state, {
            isLoading: { $set: false },
            data: {
                $set: state.data.filter(function(item) {
                    return !deletedSet[item.id];
                }),
            },
        });
    },

    [BULK_DELETE.FAIL]: (state, action) => {
        return update(state, {
            isLoading: { $set: false },
        });
    },

    [IGNORE_STATEMENTS.SUCCESS]: (state, action) => {
        var ignoredIds = action.meta.previousAction.ids;
        var ignoredSet = {};
        ignoredIds.forEach(function(id) { ignoredSet[id] = true; });
        return update(state, {
            data: {
                $set: state.data.filter(function(item) {
                    return !ignoredSet[item.id];
                }),
            },
        });
    },

    [UNIGNORE_STATEMENTS.SUCCESS]: (state, action) => {
        return state;
    },

    [AUTO_RECONCILE.INIT]: (state, action) => {
        return update(state, {
            isAutoReconciling: { $set: true },
        });
    },

    [AUTO_RECONCILE.SUCCESS]: (state, action) => {
        return update(state, {
            isAutoReconciling: { $set: false },
        });
    },

    [AUTO_RECONCILE.FAIL]: (state, action) => {
        return update(state, {
            isAutoReconciling: { $set: false },
        });
    },

    [GET_STATS.SUCCESS]: (state, action) => {
        return update(state, {
            stats: { $set: action.payload.data.data },
        });
    },

    // OPTIMIZED: Use index-based update instead of O(n) map
    [CREATE_AND_RECONCILE.SUCCESS]: (state, action) => {
        var statementId = action.meta.previousAction.statementId;
        var index = state.data.findIndex(function(item) { return item.id === statementId; });

        if (index === -1) {
            // Item not found, just update drawer
            return update(state, {
                drawer: {
                    visible: { $set: false },
                    data: { $set: null },
                },
            });
        }

        // Response shape (after fix): always an array of { entry, reconciliation }.
        // Also handles legacy single { entry, reconciliation } for backward compat.
        var payloadData = action.payload.data.data;
        var arr = Array.isArray(payloadData) ? payloadData : (payloadData ? [payloadData] : []);
        var reconciliations = arr
            .map(function(p) { return p && p.reconciliation ? p.reconciliation : null; })
            .filter(Boolean);
        console.log('[createAndReconcile] received', reconciliations.length, 'reconciliations, payload:', action.payload.data);
        return update(state, {
            data: {
                [index]: {
                    status: { $set: 'reconciled' },
                    reconciliations: { $set: reconciliations },
                },
            },
            drawer: {
                visible: { $set: false },
                data: { $set: null },
            },
        });
    },

    [LOAD_IMPORTS.INIT]: (state, action) => {
        return update(state, {
            imports: {
                isLoading: { $set: true },
            },
        });
    },

    [LOAD_IMPORTS.SUCCESS]: (state, action) => {
        return update(state, {
            imports: {
                isLoading: { $set: false },
                data: { $set: action.payload.data.data },
            },
        });
    },

    [LOAD_IMPORTS.FAIL]: (state, action) => {
        return update(state, {
            imports: {
                isLoading: { $set: false },
            },
        });
    },

    [DELETE_IMPORT.INIT]: (state, action) => {
        return update(state, {
            imports: {
                isLoading: { $set: true },
            },
        });
    },

    [DELETE_IMPORT.SUCCESS]: (state, action) => {
        var importId = action.meta.previousAction.importId;
        return update(state, {
            imports: {
                isLoading: { $set: false },
                data: {
                    $set: state.imports.data.filter(function(item) {
                        return item.id !== importId;
                    }),
                },
            },
        });
    },

    [DELETE_IMPORT.FAIL]: (state, action) => {
        return update(state, {
            imports: {
                isLoading: { $set: false },
            },
        });
    },
};

var reducer = function(state, action) {
    if (state === undefined) state = defaultState;

    var handler = ACTION_HANDLERS[action.type];
    if (!handler) return state;

    var newState = handler(state, action);

    var updateCache = [
        LOAD_BANK_STATEMENTS.SUCCESS,
    ].includes(action.type);

    if (updateCache) {
        LocalCacheHandler.save({
            pathname: DEFAULT_CACHE_KEY,
            data: newState,
        });
    }

    return newState;
};

export default reducer;
